/**
 * CampusUploadPreviewPage — V2 preview page for master file upload.
 *
 * Displays staged data (Areas, Residents, Devices) in three tabs so the user
 * can review before committing to production.  Submit finalises the upload;
 * Cancel navigates back to the campus profile.
 *
 * Props:
 *   campusId  — target campus ID (string from URL param)
 *   uploadKey — unique staging session key (string from URL param)
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import usePersistedPerPage from '../../../../hooks/usePersistedPerPage.js';
import './campus-upload-preview-page.css';
import NavigationV2 from '../../../../components/v2/NavigationV2.jsx';

import { getCampusId, getUserId } from '../../../../services/session.js';

import {
  fetchUploadStatus,
  fetchStagingAreas,
  fetchStagingResidents,
  fetchStagingDevices,
  apiUploadFinalize,
} from '../campus-profile/api/campusProfileApi.js';

import {
  exportStagingAreasCsv,
  exportStagingResidentsCsv,
  exportStagingDevicesCsv,
} from '../../../../services/csvExport.js';

// ===========================================================================
// COLUMN DEFINITIONS
// ===========================================================================

const AREA_COLUMNS = [
  { key: 'area_name', label: 'Name', sortable: true },
  { key: 'area_type_description', label: 'Area Type', sortable: true },
  { key: 'resident_name', label: 'Residents', sortable: true },
  { key: 'device_name', label: 'Devices', sortable: true },
];

const RESIDENT_COLUMNS = [
  { key: 'resident_first_name', label: 'First Name', sortable: true },
  { key: 'resident_last_name', label: 'Last Name', sortable: true },
  { key: 'area_name', label: 'Area', sortable: true },
  { key: 'resident_phone', label: 'Phone #', sortable: true },
  { key: 'resident_email', label: 'Email', sortable: true },
];

const DEVICE_COLUMNS = [
  { key: 'device_name', label: 'Name', sortable: true },
  { key: 'descriptions', label: 'Description', sortable: true },
  { key: 'model_name', label: 'Model', sortable: true },
  { key: 'manufacture_uuid', label: 'Serial Number', sortable: true },
  { key: 'resident_name', label: 'Resident', sortable: true },
  { key: 'area_name', label: 'Area', sortable: true },
];

// ===========================================================================
// COMPONENT
// ===========================================================================

const CampusUploadPreviewPage = ({ campusId: campusIdProp, uploadKey }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const campusId = useMemo(() => (campusIdProp ? parseInt(campusIdProp, 10) : 0), [campusIdProp]);
  const myCampusId = useMemo(() => getCampusId(), []);

  // --------------------------------------------------------------------------
  // TOAST
  // --------------------------------------------------------------------------
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const showToast = useCallback((type, message) => {
    clearTimeout(toastTimerRef.current);
    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => setToast(null), 6000);
  }, []);

  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  // Show incoming state messages (from campus profile navigation)
  useEffect(() => {
    if (location.state?.message) {
      const msg = location.state.message;
      if (msg.success) showToast('success', msg.success);
      if (msg.error) showToast('error', msg.error);
    }
  }, [location.state, showToast]);

  // --------------------------------------------------------------------------
  // DATA STATE
  // --------------------------------------------------------------------------
  const [keyValid, setKeyValid] = useState(false);
  const [areaData, setAreaData] = useState([]);
  const [resData, setResData] = useState([]);
  const [deviceData, setDeviceData] = useState([]);
  const [loading, setLoading] = useState(true);

  // --------------------------------------------------------------------------
  // TAB + TABLE STATE
  // --------------------------------------------------------------------------
  const [activeTab, setActiveTab] = useState('area');
  const [search, setSearch] = useState('');

  // Sort state per tab
  const [areaSortCol, setAreaSortCol] = useState('area_name');
  const [areaSortOrder, setAreaSortOrder] = useState('asc');
  const [resSortCol, setResSortCol] = useState('resident_first_name');
  const [resSortOrder, setResSortOrder] = useState('asc');
  const [deviceSortCol, setDeviceSortCol] = useState('device_name');
  const [deviceSortOrder, setDeviceSortOrder] = useState('asc');

  // Pagination state per tab
  const [areaPage, setAreaPage] = useState(1);
  const [resPage, setResPage] = useState(1);
  const [devicePage, setDevicePage] = useState(1);
  const [perPage, setPerPage] = usePersistedPerPage();

  // --------------------------------------------------------------------------
  // SUBMIT STATE
  // --------------------------------------------------------------------------
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const pollRef = useRef(null);

  // Cleanup polling on unmount
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // --------------------------------------------------------------------------
  // DATA LOADING
  // --------------------------------------------------------------------------
  const validateAndLoad = useCallback(async () => {
    try {
      // Validate upload key
      const statusRows = await fetchUploadStatus(uploadKey);
      if (!statusRows || statusRows.length === 0) {
        navigate(`/profiles/campus/${campusId}`, {
          state: { message: { success: '', error: 'Invalid upload key.' } },
        });
        return;
      }
      setKeyValid(true);

      // Fetch all three staging datasets in parallel
      const [areas, residents, devices] = await Promise.all([
        fetchStagingAreas(campusId),
        fetchStagingResidents(campusId),
        fetchStagingDevices(campusId),
      ]);

      setAreaData(areas || []);
      setResData(residents || []);
      setDeviceData(devices || []);
    } catch (err) {
      console.error('[CampusUploadPreviewPage] load error:', err);
      showToast('error', 'Failed to load staging data.');
    } finally {
      setLoading(false);
    }
  }, [uploadKey, campusId, navigate, showToast]);

  useEffect(() => {
    validateAndLoad();
  }, [validateAndLoad]);

  // --------------------------------------------------------------------------
  // SEARCH + SORT + PAGINATION PIPELINE (generic per tab)
  // --------------------------------------------------------------------------
  const buildPipeline = (data, columns, searchTerm, sortCol, sortOrd) => {
    let result = [...data];

    // 1. Search
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter((row) =>
        columns.some((col) => (row[col.key] || '').toString().toLowerCase().includes(term))
      );
    }

    // 2. Sort
    result.sort((a, b) => {
      const aVal = a[sortCol] || '';
      const bVal = b[sortCol] || '';
      const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
      return sortOrd === 'asc' ? cmp : -cmp;
    });

    return result;
  };

  const filteredAreas = useMemo(() => buildPipeline(areaData, AREA_COLUMNS, search, areaSortCol, areaSortOrder), [areaData, search, areaSortCol, areaSortOrder]);
  const filteredRes = useMemo(() => buildPipeline(resData, RESIDENT_COLUMNS, search, resSortCol, resSortOrder), [resData, search, resSortCol, resSortOrder]);
  const filteredDevices = useMemo(() => buildPipeline(deviceData, DEVICE_COLUMNS, search, deviceSortCol, deviceSortOrder), [deviceData, search, deviceSortCol, deviceSortOrder]);

  // Reset page on search/perPage changes
  useEffect(() => { setAreaPage(1); setResPage(1); setDevicePage(1); }, [search, perPage]);

  // --------------------------------------------------------------------------
  // PAGINATION HELPERS
  // --------------------------------------------------------------------------
  const paginate = (data, page) => {
    const total = data.length;
    const totalPages = Math.max(1, Math.ceil(total / perPage));
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * perPage;
    const paginated = data.slice(start, start + perPage);
    const showingStart = total === 0 ? 0 : start + 1;
    const showingEnd = Math.min(safePage * perPage, total);

    // Page number buttons
    const maxButtons = 5;
    let pStart = Math.max(1, safePage - Math.floor(maxButtons / 2));
    let pEnd = Math.min(totalPages, pStart + maxButtons - 1);
    if (pEnd - pStart < maxButtons - 1) pStart = Math.max(1, pEnd - maxButtons + 1);
    const pageNumbers = [];
    for (let i = pStart; i <= pEnd; i++) pageNumbers.push(i);

    return { paginated, total, totalPages, safePage, showingStart, showingEnd, pageNumbers };
  };

  const areaPag = useMemo(() => paginate(filteredAreas, areaPage), [filteredAreas, areaPage, perPage]);
  const resPag = useMemo(() => paginate(filteredRes, resPage), [filteredRes, resPage, perPage]);
  const devicePag = useMemo(() => paginate(filteredDevices, devicePage), [filteredDevices, devicePage, perPage]);

  // --------------------------------------------------------------------------
  // SORT HANDLER
  // --------------------------------------------------------------------------
  const handleSort = useCallback((tab, column) => {
    if (tab === 'area') {
      setAreaSortCol((prev) => { if (prev === column) { setAreaSortOrder((o) => o === 'asc' ? 'desc' : 'asc'); return prev; } setAreaSortOrder('asc'); return column; });
    } else if (tab === 'resident') {
      setResSortCol((prev) => { if (prev === column) { setResSortOrder((o) => o === 'asc' ? 'desc' : 'asc'); return prev; } setResSortOrder('asc'); return column; });
    } else {
      setDeviceSortCol((prev) => { if (prev === column) { setDeviceSortOrder((o) => o === 'asc' ? 'desc' : 'asc'); return prev; } setDeviceSortOrder('asc'); return column; });
    }
  }, []);

  // --------------------------------------------------------------------------
  // EXPORT HANDLERS
  // --------------------------------------------------------------------------
  const handleExport = useCallback(() => {
    if (activeTab === 'area' && filteredAreas.length) exportStagingAreasCsv(filteredAreas);
    else if (activeTab === 'resident' && filteredRes.length) exportStagingResidentsCsv(filteredRes);
    else if (activeTab === 'device' && filteredDevices.length) exportStagingDevicesCsv(filteredDevices);
  }, [activeTab, filteredAreas, filteredRes, filteredDevices]);

  // --------------------------------------------------------------------------
  // SUBMIT FINALIZATION
  // --------------------------------------------------------------------------
  const handleSubmit = useCallback(async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    const userId = getUserId();

    try {
      const data = await apiUploadFinalize({ uploadKey, campusId, userId });
      if (!data) {
        showToast('error', 'Failed to initiate finalization.');
        setSubmitting(false);
        return;
      }

      showToast('info', 'File upload finalizing, awaiting results...');

      // Poll every 5s for up to 120s
      let elapsed = 0;
      pollRef.current = setInterval(async () => {
        elapsed += 5;
        try {
          const statusRows = await fetchUploadStatus(uploadKey);
          if (statusRows && statusRows.length > 0) {
            if (statusRows[0]?.submit_complete === 1) {
              clearInterval(pollRef.current);
              pollRef.current = null;
              navigate(`/profiles/campus/${campusId}`, {
                state: { message: { success: 'Upload completed successfully.', error: '' } },
              });
              return;
            }
            if (statusRows[0]?.submit_error === 1) {
              clearInterval(pollRef.current);
              pollRef.current = null;
              showToast('error', 'Error encountered on the backend. Please contact software support.');
              setSubmitting(false);
              return;
            }
          }
        } catch {
          // continue polling on transient errors
        }

        if (elapsed >= 120) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          showToast('error', 'Timed out finishing, please try again.');
          setSubmitting(false);
        }
      }, 5000);
    } catch (err) {
      console.error('[CampusUploadPreviewPage] submit error:', err);
      showToast('error', 'Finalization request failed.');
      setSubmitting(false);
    }
  }, [uploadKey, campusId, navigate, showToast]);

  // --------------------------------------------------------------------------
  // SORT ICON RENDERER
  // --------------------------------------------------------------------------
  const renderSortIcon = (columnKey, currentSortCol, currentSortOrder) => {
    if (currentSortCol !== columnKey) {
      return (
        <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--neutral" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
        </svg>
      );
    }
    return currentSortOrder === 'asc' ? (
      <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
      </svg>
    ) : (
      <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
      </svg>
    );
  };

  // --------------------------------------------------------------------------
  // TABLE RENDERER (reused across all 3 tabs)
  // --------------------------------------------------------------------------
  const renderTable = (columns, tabKey, sortCol, sortOrder, pag, setPageFn) => {
    if (pag.paginated.length === 0) {
      return (
        <div className="ss-empty" style={{ marginTop: 'var(--space-4)' }}>
          <p className="ss-empty__text">
            {search.trim() ? 'No rows match your search.' : 'No data in this tab.'}
          </p>
        </div>
      );
    }

    return (
      <>
        <div className="ap-table-wrap">
          <table className="ap-table">
            <thead className="ap-thead">
              <tr>
                {columns.map((col) => (
                  <th key={col.key} scope="col" className="ap-th">
                    <div className="ap-th__content">
                      {col.sortable ? (
                        <button
                          type="button"
                          onClick={() => handleSort(tabKey, col.key)}
                          className="ap-th__sort-btn"
                          aria-label={`Sort by ${col.label}`}
                        >
                          {col.label}
                          {renderSortIcon(col.key, sortCol, sortOrder)}
                        </button>
                      ) : (
                        <span>{col.label}</span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="ap-tbody">
              {pag.paginated.map((row, idx) => (
                <tr key={idx} className="ap-tr">
                  {columns.map((col) => (
                    <td key={col.key} className="ap-td">
                      <span className="ap-cell-text">{row[col.key] || '\u2014'}</span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pag.total > 0 && (
          <div className="ap-pagination">
            <p className="ap-pagination__info">
              Showing <span className="ap-pagination__emphasis">{pag.showingStart}</span>&ndash;<span className="ap-pagination__emphasis">{pag.showingEnd}</span> of{' '}
              <span className="ap-pagination__emphasis">{pag.total}</span> rows
            </p>
            <div className="ap-pagination__controls">
              <div className="ap-per-page">
                <label htmlFor={`${tabKey}-per-page`} className="ap-per-page__label">Rows per page:</label>
                <select
                  id={`${tabKey}-per-page`}
                  value={perPage}
                  onChange={(e) => setPerPage(parseInt(e.target.value, 10))}
                  className="ap-per-page__select"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={300}>300</option>
                </select>
              </div>
              <nav className="ap-page-nav" aria-label={`${tabKey} pagination`}>
                <button
                  type="button"
                  onClick={() => setPageFn((p) => Math.max(1, p - 1))}
                  disabled={pag.safePage <= 1}
                  className="ap-page-btn"
                  aria-label="Previous page"
                >
                  <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                  </svg>
                </button>
                {pag.pageNumbers.map((num) => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setPageFn(num)}
                    className={`ap-page-num ${num === pag.safePage ? 'is-active' : ''}`}
                    aria-label={`Page ${num}`}
                    aria-current={num === pag.safePage ? 'page' : undefined}
                  >
                    {num}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setPageFn((p) => Math.min(pag.totalPages, p + 1))}
                  disabled={pag.safePage >= pag.totalPages}
                  className="ap-page-btn"
                  aria-label="Next page"
                >
                  <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        )}
      </>
    );
  };

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------
  return (
    <>
      <NavigationV2 />
      <div className="nav2-main">
      <div className="dp-page">
        {/* Toast */}
        {toast && (
          <div className={`dp-toast dp-toast--${toast.type}`}>
            <span>{toast.message}</span>
            <button className="dp-toast__close" onClick={() => setToast(null)}>{'\u2715'}</button>
          </div>
        )}

        {/* Page Header */}
        <header className="dp-page-header">
          <div className="dp-page-header__inner">
            <div className="dp-breadcrumb-row">
              <nav className="dp-breadcrumb">
                <Link to="/customers/campuses" className="dp-breadcrumb__text">Campuses</Link>
                <span className="dp-breadcrumb__separator">/</span>
                <Link to={`/profiles/campus/${campusId}`} className="dp-breadcrumb__text">Campus Profile</Link>
                <span className="dp-breadcrumb__separator">/</span>
                <span className="dp-breadcrumb__current">Upload Preview</span>
              </nav>
              <div className="cup-header-actions">
                <button
                  type="button"
                  className="dp-btn dp-btn--primary dp-btn--sm"
                  onClick={() => setConfirmOpen(true)}
                  disabled={submitting}
                >
                  {submitting ? 'Submitting...' : 'Submit'}
                </button>
                <Link
                  to={`/profiles/campus/${campusId}`}
                  className="dp-btn dp-btn--secondary dp-btn--sm"
                  style={{ textDecoration: 'none' }}
                >
                  Cancel
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Tab Bar */}
        <div className="pp-tab-bar">
          <button
            className={`pp-tab${activeTab === 'area' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('area')}
          >
            Areas
            <span className="cup-tab-count">{areaData.length}</span>
          </button>
          <button
            className={`pp-tab${activeTab === 'resident' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('resident')}
          >
            Residents
            <span className="cup-tab-count">{resData.length}</span>
          </button>
          <button
            className={`pp-tab${activeTab === 'device' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('device')}
          >
            Devices
            <span className="cup-tab-count">{deviceData.length}</span>
          </button>
        </div>

        {/* Tab Content */}
        <div className="pp-tab-content">
          {loading ? (
            <div className="pp-section">
              <div className="pp-section__body">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="dp-skeleton" style={{ width: i % 2 === 0 ? '100%' : '70%', height: '16px' }} />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Controls row */}
              <div className="ap-controls">
                <div className="ap-controls__left">
                  <div className="ap-search">
                    <svg className="ap-icon-md ap-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                    </svg>
                    <input
                      type="search"
                      placeholder="Search..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="ap-search__input"
                      aria-label="Search staged data"
                    />
                  </div>
                </div>
                <div className="ap-controls__right">
                  <button type="button" className="ap-btn ap-btn--secondary" onClick={handleExport} aria-label="Export current tab">
                    <svg className="ap-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                    </svg>
                    Export
                  </button>
                </div>
              </div>

              {/* Tab tables */}
              {activeTab === 'area' && renderTable(AREA_COLUMNS, 'area', areaSortCol, areaSortOrder, areaPag, setAreaPage)}
              {activeTab === 'resident' && renderTable(RESIDENT_COLUMNS, 'resident', resSortCol, resSortOrder, resPag, setResPage)}
              {activeTab === 'device' && renderTable(DEVICE_COLUMNS, 'device', deviceSortCol, deviceSortOrder, devicePag, setDevicePage)}
            </>
          )}
        </div>

        {/* Confirm Submit Dialog */}
        {confirmOpen && (
          <div className="pp-dialog-backdrop" onClick={() => { if (!submitting) setConfirmOpen(false); }}>
            <div className="pp-dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Confirm Upload">
              <div className="pp-dialog__header">
                <h2 className="pp-dialog__title">Confirm Upload</h2>
                <button className="dp-drawer__close" onClick={() => { if (!submitting) setConfirmOpen(false); }}>{'\u2715'}</button>
              </div>
              <div className="pp-dialog__body">
                <p style={{ margin: 0, lineHeight: 1.6 }}>
                  Have you reviewed all of the data and confirmed it looks right? Are you sure you want to apply this data to the community?
                </p>
              </div>
              <div className="pp-dialog__footer">
                <button className="dp-btn dp-btn--cancel" onClick={() => setConfirmOpen(false)} disabled={submitting}>No</button>
                <button className="dp-btn dp-btn--primary" onClick={handleSubmit} disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Yes'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
      </div>
    </>
  );
};

export default CampusUploadPreviewPage;
